import { useState } from 'react'
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import clsx from 'clsx'

/**
 * DataTable Component
 * Reusable sortable table with pagination, search, and custom rendering
 */
export default function DataTable({
  columns,
  data = [],
  onSort,
  sortKey,
  sortOrder,
  loading = false,
  pageSize = 25,
  onRowClick,
  emptyMessage = 'No data available',
}) {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')

  const handleSort = (key) => {
    if (onSort) {
      onSort(key)
    }
  }

  // Filter data based on search
  const filteredData = data.filter((row) =>
    columns.some((col) => {
      const value = row[col.key]
      return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    })
  )

  // Paginate data
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const startIdx = (currentPage - 1) * pageSize
  const paginatedData = filteredData.slice(startIdx, startIdx + pageSize)

  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)))
  }

  if (loading) {
    return (
      <div className="card">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-pawssible-slate-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="card flex flex-col items-center justify-center py-12">
        <div className="text-pawssible-slate-500 text-center">
          <p className="text-lg font-medium">{emptyMessage}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex gap-4">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setCurrentPage(1)
          }}
          className="input flex-1 max-w-sm"
        />
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-pawssible-slate-200">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  className={clsx(
                    'px-6 py-3 text-left text-xs font-semibold text-pawssible-slate-600 uppercase tracking-wider',
                    col.sortable && 'cursor-pointer hover:bg-pawssible-slate-50'
                  )}
                >
                  <div className="flex items-center gap-2">
                    {col.label}
                    {col.sortable && sortKey === col.key && (
                      sortOrder === 'asc' ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, idx) => (
              <tr
                key={row.id || idx}
                className={clsx(
                  'border-b border-pawssible-slate-100 transition-colors',
                  onRowClick && 'hover:bg-pawssible-slate-50 cursor-pointer'
                )}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((col) => (
                  <td key={col.key} className="px-6 py-4 text-sm text-pawssible-slate-900">
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-pawssible-slate-600">
          <p>
            Showing {startIdx + 1} to {Math.min(startIdx + pageSize, filteredData.length)} of{' '}
            {filteredData.length} results
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 hover:bg-pawssible-slate-100 rounded disabled:opacity-50"
            >
              <ChevronLeft size={18} />
            </button>
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              const pageNum = i + 1
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={clsx(
                    'px-3 py-1 rounded text-sm font-medium transition-colors',
                    currentPage === pageNum
                      ? 'bg-pawssible-teal-600 text-white'
                      : 'hover:bg-pawssible-slate-100'
                  )}
                >
                  {pageNum}
                </button>
              )
            })}
            {totalPages > 5 && <span className="text-pawssible-slate-400">...</span>}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 hover:bg-pawssible-slate-100 rounded disabled:opacity-50"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
